import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import "./home.css";
import T from "./i18n";
import SEO from "./seo";
import PAGES from "./pages.json";
import CONTENT, { RELATED } from "./content";

const TELEGRAM_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
const TELEGRAM_CHAT_ID = process.env.REACT_APP_TELEGRAM_CHAT_ID;
const TELEGRAM_API = `https://api.telegram.org/bot${TELEGRAM_TOKEN}/sendMessage`;

export const SITE_URL = "https://gps.allsystems.uz";
export const LANGS = ["ru", "uz", "en"];
const OG_LOCALE = { ru: "ru_RU", uz: "uz_UZ", en: "en_US" };

const PHONE = "+998 93 099 60 60";
const PHONE_LINK = "tel:+998930996060";
const EMAIL = "allsystems.uz@gmail.com";

/* link to the sister site (software studio) */
const STUDIO = {
    ru: "Разработка сайтов, CRM и программ",
    uz: "Sayt, CRM va dasturlar ishlab chiqish",
    en: "Websites, CRM and software development",
};

// TODO: вставьте ссылки на соцсети (пустая ссылка ведёт на форму заявки)
const SOCIALS = [
    { label: "Instagram", icon: "fa-brands fa-instagram", url: "" },
    { label: "Telegram", icon: "fa-brands fa-telegram", url: "" },
    { label: "Facebook", icon: "fa-brands fa-facebook-f", url: "" },
];

/* Pages linked from the footer (internal links help search engines find every page) */
const POPULAR = [
    "gps_nukus", "gps_urgench", "gps_khorezm", "fuel", "sensor", "wialon", "teltonika",
    "video", "camera_install", "hikvision", "dahua", "truck", "faceid", "fleet",
];

/* ============================================================
   HELPERS
   ============================================================ */

const pageByKey = (key) => PAGES.find((p) => p.key === key) || PAGES[0];
export const pagePath = (lang, key) => {
    const { slug } = pageByKey(key);
    return `/${lang}${slug ? `/${slug}` : ""}`;
};

/* "**bold**" → <strong>bold</strong> */
const Rich = ({ text }) =>
    String(text)
        .split(/(\*\*[^*]+\*\*)/g)
        .map((part, i) =>
            part.startsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
        );

function setMeta(attr, name, content) {
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
    const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (hreflang) el.setAttribute("hreflang", hreflang);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function setJsonLd(id, data) {
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

const CITY = {
    ru: ["Нукус", "Каракалпакстан"],
    uz: ["Nukus", "Qoraqalpog‘iston"],
    en: ["Nukus", "Karakalpakstan"],
};

/* title, description, canonical, hreflang, Open Graph, <html lang>, structured data */
function useSeo(lang, page) {
    useEffect(() => {
        const seo = SEO[lang][page];
        const url = SITE_URL + pagePath(lang, page);

        document.documentElement.lang = lang;
        document.title = seo.title;
        setMeta("name", "description", seo.desc);
        setLink("canonical", url);
        LANGS.forEach((l) => setLink("alternate", SITE_URL + pagePath(l, page), l));
        setLink("alternate", SITE_URL + pagePath("ru", page), "x-default");

        setMeta("property", "og:type", "website");
        setMeta("property", "og:site_name", "ALL SYSTEMS");
        setMeta("property", "og:title", seo.title);
        setMeta("property", "og:description", seo.desc);
        setMeta("property", "og:url", url);
        setMeta("property", "og:locale", OG_LOCALE[lang]);
        setMeta("property", "og:image", `${SITE_URL}/logo512.png`);

        const c = CONTENT[lang];
        setJsonLd("ld-org", {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "ALL SYSTEMS",
            url: SITE_URL + pagePath(lang, "home"),
            logo: `${SITE_URL}/logo512.png`,
            image: `${SITE_URL}/logo512.png`,
            description: SEO[lang].home.desc,
            telephone: "+998930996060",
            email: EMAIL,
            address: { "@type": "PostalAddress", addressLocality: CITY[lang][0], addressRegion: CITY[lang][1], addressCountry: "UZ" },
            areaServed: ["Nukus", "Karakalpakstan", "Urgench", "Khorezm"],
            openingHours: ["Mo-Fr 09:00-18:00", "Sa 09:00-12:30"],
            sameAs: ["https://allsystems.uz"],
        });
        setJsonLd(
            "ld-page",
            page === "home"
                ? {
                      "@context": "https://schema.org",
                      "@type": "FAQPage",
                      mainEntity: c.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
                  }
                : {
                      "@context": "https://schema.org",
                      "@type": "BreadcrumbList",
                      itemListElement: [
                          { "@type": "ListItem", position: 1, name: c.ui.home, item: SITE_URL + pagePath(lang, "home") },
                          { "@type": "ListItem", position: 2, name: seo.h1, item: url },
                      ],
                  }
        );
    }, [lang, page]);
}

/* ============================================================
   HOOKS
   ============================================================ */

function useReveal(threshold = 0.15) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold }
        );

        io.observe(el);
        return () => io.disconnect();
    }, [threshold]);

    return [ref, visible];
}

function useCounter(target, threshold = 0.5) {
    const ref = useRef(null);
    const [value, setValue] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        let timer;

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    let cur = 0;
                    const step = Math.max(1, Math.round(target / 40));
                    timer = setInterval(() => {
                        cur += step;
                        if (cur >= target) {
                            cur = target;
                            clearInterval(timer);
                        }
                        setValue(cur);
                    }, 28);
                    io.unobserve(entry.target);
                });
            },
            { threshold }
        );

        io.observe(el);
        return () => {
            io.disconnect();
            clearInterval(timer);
        };
    }, [target, threshold]);

    return [ref, value];
}

/* Wraps children in a block that fades/slides in once scrolled into view. */
const Reveal = ({ as: Tag = "div", className = "", children, ...rest }) => {
    const [ref, visible] = useReveal();
    const classes = ["reveal", visible ? "in" : "", className].filter(Boolean).join(" ");
    return (
        <Tag ref={ref} className={classes} {...rest}>
            {children}
        </Tag>
    );
};

const Stat = ({ target, suffix = "", label }) => {
    const [revealRef, visible] = useReveal();
    const [countRef, value] = useCounter(target);
    return (
        <div ref={revealRef} className={`stat reveal${visible ? " in" : ""}`}>
            <b ref={countRef}>
                {value}
                {suffix && <em>{suffix}</em>}
            </b>
            <span>{label}</span>
        </div>
    );
};

/* ============================================================
   DATA (language-independent: icons, images, files)
   ============================================================ */

const NAV = [
    { href: "#about", key: "about" },
    { href: "#capabilities", key: "capabilities" },
    { href: "#equipment", key: "equipment" },
    { href: "#cameras", key: "cameras" },
    { href: "#contact", key: "contact" },
];

const STATS = [
    { target: 40, suffix: "%" },
    { target: 24, suffix: "/7" },
    { target: 132, suffix: "" },
];

const CAP_ICONS = [
    "fa-solid fa-location-dot",
    "fa-solid fa-gas-pump",
    "fa-solid fa-road",
    "fa-solid fa-draw-polygon",
    "fa-solid fa-gauge-high",
    "fa-solid fa-user-tie",
    "fa-solid fa-screwdriver-wrench",
    "fa-solid fa-chart-line",
];

const EQUIPMENT = [
    { img: "/logoproducts/FMB920.png", pdf: "/logoproducts/FMB920.pdf" },
    { img: "/logoproducts/FMB125.png", pdf: "/logoproducts/FMB125.pdf" },
    { img: "/logoproducts/TD150.png", pdf: "/logoproducts/TD150.pdf" },
    { img: "/logoproducts/TDBLE.png", pdf: "/logoproducts/TDBLE.pdf" },
];

const CAMERAS = [
    { brand: "HOWEN", img: "/logocameras/2sidesd.png" },
    { brand: "HOWEN", img: "/logocameras/ptz.png" },
    { brand: "HOWEN", img: "/logocameras/2side.png" },
    { brand: "HIKVISION", img: "/logocameras/hikvision-dome.png" },
    { brand: "HIKVISION", img: "/logocameras/hikvision-bullet.png" },
    { brand: "HIKVISION", img: "/logocameras/hikvision-ptz.png" },
    { brand: "DAHUA", img: "/logocameras/dahua-bullet.png" },
    { brand: "DAHUA", img: "/logocameras/dahua-dome.png" },
    { brand: "DAHUA", img: "/logocameras/dahua-ai.png" },
];

const ADV_ICONS = [
    "fa-solid fa-star",
    "fa-solid fa-shield-halved",
    "fa-solid fa-globe",
    "fa-solid fa-truck-fast",
    "fa-solid fa-headset",
    "fa-solid fa-list-check",
];

/* Russian labels for the Telegram message (operators read it in Russian) */
const TG_SERVICE = { gps: "GPS-мониторинг", cameras: "Камеры видеонаблюдения" };
const TG_INSTALL = { car: "Автомобиль", object: "Помещение / объект" };

/* ============================================================
   PAGE
   ============================================================ */

const Home = ({ lang = "ru", page = "home" }) => {
    const t = T[lang] || T.ru;
    const seo = (SEO[lang] || SEO.ru)[page] || SEO.ru.home;
    const pageInfo = pageByKey(page);
    const location = useLocation();

    const [menuOpen, setMenuOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [serviceType, setServiceType] = useState("gps");
    const [heroVisualRef, heroVisualVisible] = useReveal();
    const [openFaq, setOpenFaq] = useState(0);
    const c = CONTENT[lang] || CONTENT.ru;
    const pageText = c.pages[page];
    const isCity = ["gps_nukus", "gps_khorezm", "gps_urgench", "camera_install"].includes(page);

    useSeo(lang, page);

    const closeMenu = () => setMenuOpen(false);
    const isHome = page === "home";

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.target;
        /* escape user input: the message is sent with parse_mode HTML */
        const esc = (v) => String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        const data = Object.fromEntries([...new FormData(form).entries()].map(([k, v]) => [k, esc(v)]));

        const extraLine =
            data.serviceType === "cameras"
                ? `<b>🎥 Тип установки:</b> ${TG_INSTALL[data.installType] || "-"}\n`
                : `<b>🚚 Кол-во ТС:</b> ${data.vehiclesCount || "-"}\n`;

        const text =
            `<b>🆘 Новая заявка с сайта ALL SYSTEMS</b>\n\n` +
            `<b>🏢 Компания:</b> ${data.company || "-"}\n` +
            `<b>🆔 Имя:</b> ${data.fullName || "-"}\n` +
            `<b>📲 Телефон:</b> ${data.phone || "-"}\n` +
            `<b>💌 E-mail:</b> ${data.email || "-"}\n` +
            `<b>🛠 Услуга:</b> ${TG_SERVICE[data.serviceType] || "-"}\n` +
            extraLine +
            `\n<b>💬 Сообщение:</b> ${data.message || "-"}\n\n` +
            `<b>🌐 Язык:</b> ${lang.toUpperCase()} · <b>📄 Страница:</b> ${location.pathname}`;

        setSending(true);
        try {
            await axios.post(TELEGRAM_API, {
                chat_id: TELEGRAM_CHAT_ID,
                parse_mode: "HTML",
                text,
            });
            setSubmitted(true);
            form.reset();
            setServiceType("gps");
        } catch (err) {
            console.error("Telegram send failed:", err);
            alert(`${t.contact.error} ${PHONE}`);
        } finally {
            setSending(false);
        }
    };

    const LangSwitch = ({ className = "" }) => (
        <div className={`lang-switch ${className}`}>
            {LANGS.map((l) => (
                <Link
                    key={l}
                    to={pagePath(l, page)}
                    hrefLang={l}
                    lang={l}
                    title={T[l].langName}
                    className={l === lang ? "active" : ""}
                    onClick={closeMenu}
                >
                    {l.toUpperCase()}
                </Link>
            ))}
        </div>
    );

    return (
        <>
            {/* ============ HEADER ============ */}
            <header>
                <div className="wrap nav">
                    <Link to={pagePath(lang, "home")} className="logo" aria-label={`ALL SYSTEMS — ${t.home}`}>
                        <img src="/logo.png" alt="ALL SYSTEMS" />
                    </Link>

                    <nav className={`nav-links${menuOpen ? " open" : ""}`}>
                        {NAV.map((link) => (
                            <a key={link.href} href={link.href} onClick={closeMenu}>
                                {t.nav[link.key]}
                            </a>
                        ))}
                        <a href={PHONE_LINK} className="nav-links-phone" onClick={closeMenu}>
                            <i className="fa-solid fa-phone"></i> {PHONE}
                        </a>
                    </nav>

                    <div className="nav-cta">
                        <LangSwitch />
                        <a href={PHONE_LINK} className="nav-phone">
                            <i className="fa-solid fa-phone"></i>
                            {PHONE}
                        </a>
                        <a href="#contact" className="btn btn-primary btn-sm">{t.cta}</a>
                        <button
                            className={`burger${menuOpen ? " active" : ""}`}
                            aria-label={t.menu}
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen((o) => !o)}
                        >
                            <span></span><span></span><span></span>
                        </button>
                    </div>
                </div>
            </header>

            <main>
                {/* ============ HERO ============ */}
                <section className="hero">
                    <div className="wrap hero-grid">
                        <div className="hero-copy">
                            {!isHome && (
                                <nav className="crumbs" aria-label="breadcrumb">
                                    <Link to={pagePath(lang, "home")}>{c.ui.home}</Link>
                                    <i className="fa-solid fa-chevron-right"></i>
                                    <span>{c.short[page]}</span>
                                </nav>
                            )}
                            {/* page-specific, visible H1 from SEO */}
                            <h1 className="eyebrow hero-h1">{seo.h1}</h1>
                            <p className="hero-title">
                                {t.hero.slogan} <span className="hl">{t.hero.sloganHl}</span>
                            </p>
                            <p className="lead">{isHome ? t.hero.lead : seo.desc}</p>
                            <div className="hero-ctas">
                                <a href="#contact" className="btn btn-primary">
                                    {t.hero.ctaPrimary} <i className="fa-solid fa-arrow-right"></i>
                                </a>
                                <a href={isHome ? `#${pageInfo.section}` : "#focus"} className="btn btn-ghost">
                                    {isHome ? t.hero.ctaSecondary : t.hero.more}
                                </a>
                            </div>
                            <div className="hero-stats">
                                {STATS.map((s, i) => (
                                    <Stat key={i} target={s.target} suffix={s.suffix} label={t.hero.stats[i]} />
                                ))}
                            </div>
                        </div>

                        <div ref={heroVisualRef} className={`hero-visual reveal${heroVisualVisible ? " in" : ""}`}>
                            <div className="panel-frame">
                                <div className="panel-frame-head">
                                    <span className="label">
                                        <i className="live-dot"></i> {t.hero.panel}
                                    </span>
                                    <div className="dots"><span></span><span></span><span></span></div>
                                </div>

                                <svg viewBox="0 0 560 300" width="100%" style={{ height: "auto" }} role="img" aria-label={t.hero.svgLabel}>
                                    <path className="route-line" d="M92,210 L253,210" />
                                    <path className="route-line" d="M307,210 L468,210" />
                                    <path className="route-line" d="M280,70 L495,210" />

                                    <circle className="packet" r="3.5">
                                        <animateMotion dur="1.8s" begin="0s" repeatCount="indefinite" path="M92,210 L253,210" />
                                    </circle>
                                    <circle className="packet packet-alt" r="3.5">
                                        <animateMotion dur="1.8s" begin="-.9s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear" path="M92,210 L253,210" />
                                    </circle>
                                    <circle className="packet" r="3.5">
                                        <animateMotion dur="1.8s" begin="-.6s" repeatCount="indefinite" path="M307,210 L468,210" />
                                    </circle>
                                    <circle className="packet packet-alt" r="3.5">
                                        <animateMotion dur="1.8s" begin="-1.5s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear" path="M307,210 L468,210" />
                                    </circle>
                                    <circle className="packet" r="3">
                                        <animateMotion dur="1.3s" begin="-1.2s" repeatCount="indefinite" path="M280,70 L495,210" />
                                    </circle>
                                    <circle className="packet packet-alt" r="3">
                                        <animateMotion dur="1.3s" begin="-1.9s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear" path="M280,70 L495,210" />
                                    </circle>

                                    <g transform="translate(65,210)">
                                        <circle className="badge-pulse" r="27" />
                                        <circle className="node-badge" r="27" />
                                        <rect x="-15" y="-11" width="20" height="14" rx="1.5" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
                                        <line x1="-5" y1="3" x2="-5" y2="7" stroke="var(--accent)" strokeWidth="1.6" />
                                        <line x1="-9" y1="7" x2="-1" y2="7" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" />
                                        <rect x="3" y="-2" width="9" height="15" rx="2" fill="var(--panel)" stroke="var(--accent)" strokeWidth="1.6" />
                                        <line x1="6" y1="10" x2="9" y2="10" stroke="var(--accent)" strokeWidth="1.4" strokeLinecap="round" />
                                    </g>
                                    <text x="65" y="264" textAnchor="middle" className="node-label hq">{t.hero.nodes.client}</text>

                                    <g transform="translate(280,210)">
                                        <circle className="badge-pulse" r="27" style={{ animationDelay: ".5s" }} />
                                        <circle className="node-badge" r="27" />
                                        <rect x="-13" y="-12" width="26" height="10" rx="1.5" fill="none" stroke="var(--accent)" strokeWidth="1.8" />
                                        <rect x="-13" y="1" width="26" height="10" rx="1.5" fill="none" stroke="var(--accent)" strokeWidth="1.8" />
                                        <circle cx="-7" cy="-7" r="1.2" fill="var(--accent)" />
                                        <circle cx="-7" cy="6" r="1.2" fill="var(--accent)" />
                                    </g>
                                    <text x="280" y="264" textAnchor="middle" className="node-label hq">{t.hero.nodes.server}</text>

                                    <g transform="translate(495,210)">
                                        <circle className="badge-pulse" r="27" style={{ animationDelay: "1s" }} />
                                        <circle className="node-badge" r="27" />
                                        <rect x="-16" y="-4" width="19" height="11" rx="1.5" fill="none" stroke="var(--accent)" strokeWidth="1.8" />
                                        <path d="M3,-4 h8 l6,6 v5 h-14 z" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinejoin="round" />
                                        <circle cx="-9" cy="9" r="2.6" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
                                        <circle cx="9" cy="9" r="2.6" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
                                    </g>
                                    <text x="495" y="264" textAnchor="middle" className="node-label hq">{t.hero.nodes.vehicle}</text>

                                    <g transform="translate(280,45)">
                                        <circle className="badge-pulse" r="27" style={{ animationDelay: "1.5s" }} />
                                        <circle className="node-badge" r="27" />
                                        <rect x="-6" y="-6" width="12" height="12" rx="1.5" fill="none" stroke="var(--accent)" strokeWidth="1.8" />
                                        <rect x="-19" y="-3" width="9" height="6" rx="1" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
                                        <rect x="10" y="-3" width="9" height="6" rx="1" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
                                        <line x1="-6" y1="0" x2="-10" y2="0" stroke="var(--accent)" strokeWidth="1.6" />
                                        <line x1="6" y1="0" x2="10" y2="0" stroke="var(--accent)" strokeWidth="1.6" />
                                        <line x1="0" y1="-6" x2="0" y2="-12" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" />
                                        <circle cx="0" cy="-13" r="1.3" fill="var(--accent)" />
                                    </g>
                                    <text x="240" y="41" textAnchor="end" className="node-label hq">{t.hero.nodes.gps}</text>
                                    <text x="240" y="55" textAnchor="end" className="node-label">{t.hero.nodes.satellite}</text>
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* ============ PARTNER STRIP ============ */}
                    <div className="partner-strip">
                        <div className="wrap">
                            <span className="tag">{t.partners}</span>
                            <div className="items">
                                <b>Wialon Local</b>
                                <b>Teltonika</b>
                                <b>Omnicomm</b>
                                <b>{lang === "ru" ? "Эскорт" : "Escort"}</b>
                                <b>HOWEN</b>
                                <b>Hikvision</b>
                                <b>Dahua</b>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ FOCUS (unique text of the page) ============ */}
                {!isHome && pageText && (
                    <section id="focus" className="focus">
                        <div className="wrap focus-grid">
                            <Reveal className="focus-copy">
                                <span className="eyebrow">{isCity ? c.ui.eyebrowCity : c.ui.eyebrow}</span>
                                <h2>{c.short[page]}</h2>
                                <p>{pageText.intro}</p>
                                <a href="#contact" className="btn btn-primary">
                                    {c.ui.cta} <i className="fa-solid fa-arrow-right"></i>
                                </a>
                            </Reveal>
                            <div className="focus-side">
                                <h3 className="focus-sub">{c.ui.includes}</h3>
                                <ul className="focus-points">
                                    {pageText.points.map((pt, i) => (
                                        <Reveal as="li" key={pt}>
                                            <span className="num">0{i + 1}</span>
                                            {pt}
                                        </Reveal>
                                    ))}
                                </ul>
                                <div className="focus-related">
                                    <span>{c.ui.related}:</span>
                                    {(RELATED[page] || []).map((k) => (
                                        <Link key={k} to={pagePath(lang, k)} className="pill">
                                            {c.short[k]}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* ============ ABOUT ============ */}
                <section id="about">
                    <div className="wrap">
                        <div className="about-grid">
                            <Reveal>
                                <span className="eyebrow">{t.about.eyebrow}</span>
                                <h2>{t.about.title}</h2>
                                <p className="about-lead">{t.about.lead}</p>
                                <p><Rich text={t.about.p1} /></p>
                                <p>{t.about.p2}</p>
                            </Reveal>

                            <Reveal className="flow-panel">
                                <span className="eyebrow">{t.about.how}</span>
                                <div className="flow">
                                    {t.flow.map(([title, text], i) => (
                                        <div className="flow-step" key={i}>
                                            <span className="flow-num">{String(i + 1).padStart(2, "0")}</span>
                                            <div>
                                                <h3>{title}</h3>
                                                <p>{text}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </section>

                {/* ============ CAPABILITIES ============ */}
                <section id="capabilities" className="section-alt">
                    <div className="wrap">
                        <Reveal className="section-head">
                            <span className="eyebrow">{t.cap.eyebrow}</span>
                            <h2>{t.cap.title}</h2>
                            <p>{t.cap.text}</p>
                        </Reveal>

                        <div className="cap-grid">
                            {t.capabilities.map(([title, items], i) => (
                                <Reveal className="cap-card" key={i}>
                                    <div className="icon">
                                        <i className={CAP_ICONS[i]}></i>
                                    </div>
                                    <h3>{title}</h3>
                                    <ul>
                                        {items.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </ul>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ============ EQUIPMENT ============ */}
                <section id="equipment">
                    <div className="wrap">
                        <Reveal className="section-head">
                            <span className="eyebrow">{t.eq.eyebrow}</span>
                            <h2>{t.eq.title}</h2>
                            <p>{t.eq.text}</p>
                        </Reveal>

                        <div className="eq-grid">
                            {EQUIPMENT.map((item, i) => {
                                const [title, text] = t.equipment[i];
                                return (
                                    <Reveal className="eq-card" key={item.img}>
                                        <div className="thumb">
                                            <img src={item.img} alt={title} loading="lazy" />
                                        </div>
                                        <h3>{title}</h3>
                                        <p>{text}</p>
                                        <a className="flyer" href={item.pdf} target="_blank" rel="noopener noreferrer">
                                            {t.eq.flyer} <i className="fa-solid fa-arrow-up-right-from-square"></i>
                                        </a>
                                    </Reveal>
                                );
                            })}
                        </div>

                        <Reveal className="eq-note">
                            <i className="fa-solid fa-circle-info"></i>
                            <span><Rich text={t.eq.note} /></span>
                        </Reveal>
                    </div>
                </section>

                {/* ============ CAMERAS ============ */}
                <section id="cameras" className="section-dark">
                    <div className="wrap">
                        <Reveal className="section-head">
                            <span className="eyebrow">{t.cam.eyebrow}</span>
                            <h2>{t.cam.title}</h2>
                            <p><Rich text={t.cam.text} /></p>
                        </Reveal>

                        <div className="cam-grid">
                            {CAMERAS.map((cam, i) => {
                                const [title, text] = t.cameras[i];
                                return (
                                    <Reveal className="eq-card cam-card" key={cam.img}>
                                        <span className={`cam-badge cam-badge-${cam.brand.toLowerCase()}`}>{cam.brand}</span>
                                        <div className="thumb">
                                            <img src={cam.img} alt={title} loading="lazy" />
                                        </div>
                                        <h3>{title}</h3>
                                        <p>{text}</p>
                                    </Reveal>
                                );
                            })}
                        </div>

                        <Reveal className="eq-note">
                            <i className="fa-solid fa-video"></i>
                            <span><Rich text={t.cam.note} /></span>
                        </Reveal>
                    </div>
                </section>

                {/* ============ REGION ============ */}
                <section id="region">
                    <div className="wrap region-grid">
                        <Reveal>
                            <span className="eyebrow">{t.region.eyebrow}</span>
                            <h2>{t.region.title}</h2>
                            <p className="region-text">{t.region.text}</p>
                            <div className="region-chips">
                                {t.region.chips.map((chip, i) => (
                                    <span key={chip}>
                                        {i === 0 && <i className="fa-solid fa-location-dot"></i>} {chip}
                                    </span>
                                ))}
                            </div>
                        </Reveal>

                        <Reveal className="map-panel">
                            <svg viewBox="0 0 560 300" width="100%" style={{ height: "auto" }} role="img" aria-label={t.region.svgLabel}>
                                <defs>
                                    <radialGradient id="globeGrad" cx="34%" cy="28%" r="78%">
                                        <stop offset="0%" stopColor="var(--panel)" />
                                        <stop offset="60%" stopColor="var(--panel-2)" />
                                        <stop offset="100%" stopColor="var(--bg-alt)" />
                                    </radialGradient>
                                    <radialGradient id="globeShade" cx="68%" cy="75%" r="60%">
                                        <stop offset="0%" stopColor="var(--accent)" stopOpacity=".09" />
                                        <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
                                    </radialGradient>
                                    <clipPath id="globeClip">
                                        <circle cx="280" cy="148" r="104" />
                                    </clipPath>
                                </defs>

                                <ellipse cx="280" cy="266" rx="78" ry="10" fill="var(--line)" opacity=".5" />

                                <circle cx="280" cy="148" r="134" className="route-line" fill="none" />
                                <circle r="3.6" fill="var(--accent)">
                                    <animateMotion dur="16s" repeatCount="indefinite" path="M280,14 A134,134 0 0,1 280,282 A134,134 0 0,1 280,14 Z" />
                                </circle>

                                <circle cx="280" cy="148" r="104" fill="url(#globeGrad)" stroke="var(--line)" strokeWidth="1.4" />
                                <circle cx="280" cy="148" r="104" fill="url(#globeShade)" />

                                <g clipPath="url(#globeClip)">
                                    <ellipse cx="280" cy="148" rx="104" ry="22" className="globe-lat" />
                                    <ellipse cx="280" cy="107" rx="98" ry="14" className="globe-lat" />
                                    <ellipse cx="280" cy="189" rx="98" ry="14" className="globe-lat" />
                                    <ellipse cx="280" cy="72" rx="78" ry="7" className="globe-lat" />
                                    <ellipse cx="280" cy="224" rx="78" ry="7" className="globe-lat" />
                                    <ellipse cx="280" cy="148" rx="78" ry="104" className="globe-meridian-static" />
                                    <ellipse cx="280" cy="148" rx="40" ry="104" className="globe-meridian-static" />
                                    <line x1="280" y1="44" x2="280" y2="252" stroke="var(--line)" strokeWidth="1" opacity=".7" />
                                    <ellipse cx="280" cy="148" rx="2" ry="104" className="globe-meridian-spin" />

                                    <circle cx="245" cy="185" r="2.2" className="globe-dot-dim" />
                                    <circle cx="325" cy="95" r="2.2" className="globe-dot-dim" />
                                    <circle cx="345" cy="160" r="2.2" className="globe-dot-dim" />
                                    <circle cx="308" cy="205" r="2.2" className="globe-dot-dim" />
                                    <circle cx="235" cy="125" r="2.2" className="globe-dot-dim" />
                                    <circle cx="300" cy="110" r="2.2" className="globe-dot-dim" />
                                    <circle cx="260" cy="90" r="2.2" className="globe-dot-dim" />
                                </g>
                                <circle cx="280" cy="148" r="104" fill="none" stroke="var(--line)" strokeWidth="1.4" />

                                <circle cx="280" cy="44" r="2" fill="var(--text-dim)" />
                                <circle cx="280" cy="252" r="2" fill="var(--text-dim)" />

                                <circle cx="220" cy="112" r="5" className="globe-marker" />
                                <circle cx="220" cy="112" r="5" className="globe-marker-ring" />

                                <path d="M220,112 L150,86 L112,86" className="globe-leader" />
                                <circle cx="112" cy="86" r="2.3" fill="var(--accent)" />
                                <text x="104" y="90" textAnchor="end" className="node-label hq" style={{ fontSize: "12px" }}>{t.region.city}</text>

                                <text x="502" y="286" textAnchor="end" className="node-label" style={{ fontSize: "10px", letterSpacing: ".06em" }}>
                                    {t.region.globe}
                                </text>
                            </svg>
                        </Reveal>
                    </div>
                </section>

                {/* ============ ADVANTAGES ============ */}
                <section id="advantages" className="section-alt">
                    <div className="wrap">
                        <Reveal className="section-head">
                            <span className="eyebrow">{t.adv.eyebrow}</span>
                            <h2>{t.adv.title}</h2>
                        </Reveal>

                        <div className="adv-grid">
                            {t.advantages.map(([title, text], i) => (
                                <Reveal className="adv-card" key={i}>
                                    <div className="adv-top">
                                        <div className="icon">
                                            <i className={ADV_ICONS[i]}></i>
                                        </div>
                                        <span className="adv-num">{String(i + 1).padStart(2, "0")}</span>
                                    </div>
                                    <h3>{title}</h3>
                                    <p>{text}</p>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ============ FAQ ============ */}
                <section id="faq" className="faq">
                    <div className="wrap faq-grid">
                        <Reveal className="section-head faq-head">
                            <span className="eyebrow">{c.ui.faqEyebrow}</span>
                            <h2>{c.ui.faqTitle}</h2>
                        </Reveal>
                        <div className="faq-list">
                            {c.faq.map(([q, a], i) => {
                                const open = openFaq === i;
                                return (
                                    <div key={q} className={`faq-item${open ? " open" : ""}`}>
                                        <h3>
                                            <button
                                                type="button"
                                                id={`faq-q-${i}`}
                                                aria-expanded={open}
                                                aria-controls={`faq-a-${i}`}
                                                onClick={() => setOpenFaq(open ? -1 : i)}
                                            >
                                                {q}
                                                <i className="fa-solid fa-plus"></i>
                                            </button>
                                        </h3>
                                        <div className="faq-a" id={`faq-a-${i}`} role="region" aria-labelledby={`faq-q-${i}`}>
                                            <p>{a}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* ============ CONTACT ============ */}
                <section id="contact">
                    <div className="wrap">
                        <Reveal className="section-head">
                            <span className="eyebrow">{t.contact.eyebrow}</span>
                            <h2>{t.contact.title}</h2>
                            <p>{t.contact.text}</p>
                        </Reveal>

                        <div className="contact-grid">
                            <Reveal className="form-panel">
                                {!submitted ? (
                                    <form onSubmit={handleSubmit}>
                                        <div className="form-row">
                                            <div className="field">
                                                <label htmlFor="f-company">{t.contact.company}</label>
                                                <input id="f-company" type="text" name="company" placeholder={t.contact.companyPh} />
                                            </div>
                                            <div className="field">
                                                <label htmlFor="f-name">{t.contact.name}</label>
                                                <input id="f-name" type="text" name="fullName" placeholder={t.contact.namePh} required />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="field">
                                                <label htmlFor="f-phone">{t.contact.phone}</label>
                                                <input id="f-phone" type="tel" name="phone" placeholder="+998 __ ___-__-__" required />
                                            </div>
                                            <div className="field">
                                                <label htmlFor="f-email">{t.contact.email}</label>
                                                <input id="f-email" type="email" name="email" placeholder="mail@company.uz" />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="field">
                                                <label htmlFor="f-service">{t.contact.service}</label>
                                                <select
                                                    id="f-service"
                                                    name="serviceType"
                                                    value={serviceType}
                                                    onChange={(e) => setServiceType(e.target.value)}
                                                >
                                                    <option value="gps">{t.contact.services.gps}</option>
                                                    <option value="cameras">{t.contact.services.cameras}</option>
                                                </select>
                                            </div>

                                            {serviceType === "gps" ? (
                                                <div className="field">
                                                    <label htmlFor="f-vehicles">{t.contact.vehicles}</label>
                                                    <input id="f-vehicles" type="number" name="vehiclesCount" placeholder={t.contact.vehiclesPh} min="1" />
                                                </div>
                                            ) : (
                                                <div className="field">
                                                    <label htmlFor="f-install">{t.contact.install}</label>
                                                    <select id="f-install" name="installType" defaultValue="car">
                                                        <option value="car">{t.contact.installs.car}</option>
                                                        <option value="object">{t.contact.installs.object}</option>
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                        <div className="field full">
                                            <label htmlFor="f-message">{t.contact.message}</label>
                                            <textarea id="f-message" name="message" placeholder={t.contact.messagePh} />
                                        </div>
                                        <div className="form-foot">
                                            <button type="submit" className="btn btn-primary" disabled={sending}>
                                                {sending ? t.contact.sending : t.contact.send}
                                                {!sending && <i className="fa-solid fa-paper-plane"></i>}
                                            </button>
                                            <p>{t.contact.consent}</p>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="form-success">
                                        <i className="fa-solid fa-circle-check"></i>
                                        <h3>{t.contact.successTitle}</h3>
                                        <p>{t.contact.successText}</p>
                                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSubmitted(false)}>
                                            {t.contact.again}
                                        </button>
                                    </div>
                                )}
                            </Reveal>

                            <div className="contact-side">
                                <Reveal className="info-card info-card-accent">
                                    <div className="icon">
                                        <i className="fa-solid fa-phone"></i>
                                    </div>
                                    <div>
                                        <h3>{t.contact.phoneTitle}</h3>
                                        <a href={PHONE_LINK} className="info-big">{PHONE}</a>
                                    </div>
                                </Reveal>

                                <Reveal className="info-card">
                                    <div className="icon">
                                        <i className="fa-solid fa-envelope"></i>
                                    </div>
                                    <div>
                                        <h3>{t.contact.emailTitle}</h3>
                                        <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                                    </div>
                                </Reveal>

                                <Reveal className="info-card">
                                    <div className="icon">
                                        <i className="fa-solid fa-location-dot"></i>
                                    </div>
                                    <div>
                                        <h3>{t.contact.address}</h3>
                                        <p>{t.contact.addressValue}</p>
                                    </div>
                                </Reveal>

                                <Reveal className="info-card">
                                    <div className="icon">
                                        <i className="fa-solid fa-clock"></i>
                                    </div>
                                    <div className="hours">
                                        <h3>{t.contact.hours}</h3>
                                        {t.contact.days.map(([day, time]) => (
                                            <p key={day}><span>{day}</span> {time}</p>
                                        ))}
                                        <p><span>{t.contact.sunday}</span> <em>{t.contact.dayOff}</em></p>
                                    </div>
                                </Reveal>

                                <Reveal className="social-row">
                                    {SOCIALS.map((s) => (
                                        <a
                                            key={s.label}
                                            href={s.url || "#contact"}
                                            aria-label={s.label}
                                            target={s.url ? "_blank" : undefined}
                                            rel="noopener noreferrer"
                                        >
                                            <i className={s.icon}></i>
                                        </a>
                                    ))}
                                </Reveal>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* ============ FOOTER ============ */}
            <footer>
                <div className="wrap">
                    <div className="footer-grid">
                        <div className="footer-col footer-about">
                            <Link to={pagePath(lang, "home")} className="logo" aria-label={`ALL SYSTEMS — ${t.home}`}>
                                <img src="/logo-light.png" alt="ALL SYSTEMS" />
                            </Link>
                            <p>{t.footer.about}</p>
                            <LangSwitch className="lang-switch-dark" />
                        </div>

                        <div className="footer-col">
                            <h4>{t.footer.company}</h4>
                            <ul>
                                <li><Link to={pagePath(lang, "about")}>{t.nav.about}</Link></li>
                                <li><a href="#advantages">{t.footer.why}</a></li>
                                <li><Link to={pagePath(lang, "contacts")}>{t.nav.contact}</Link></li>
                            </ul>
                        </div>

                        <div className="footer-col">
                            <h4>{t.footer.services}</h4>
                            <ul>
                                <li><Link to={pagePath(lang, "gps")}>{SEO[lang].gps.h1}</Link></li>
                                <li><Link to={pagePath(lang, "fuel")}>{SEO[lang].fuel.h1}</Link></li>
                                <li><Link to={pagePath(lang, "video")}>{SEO[lang].video.h1}</Link></li>
                                <li><Link to={pagePath(lang, "fleet")}>{SEO[lang].fleet.h1}</Link></li>
                                <li><a href="#region">{t.footer.coverage}</a></li>
                            </ul>
                        </div>

                        <div className="footer-col">
                            <h4>{t.footer.contacts}</h4>
                            <ul>
                                <li><a href={PHONE_LINK}>{PHONE}</a></li>
                                <li><a href={`mailto:${EMAIL}`}>{EMAIL}</a></li>
                                <li><a href="#contact">{t.cta}</a></li>
                            </ul>
                        </div>
                    </div>

                    <nav className="footer-seo" aria-label={t.footer.popular}>
                        <h4>{t.footer.popular}</h4>
                        <ul>
                            {POPULAR.map((key) => (
                                <li key={key}>
                                    <Link to={pagePath(lang, key)}>{SEO[lang][key].h1}</Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className="footer-bottom">
                        <p>© {new Date().getFullYear()} {t.footer.copy}</p>
                        <a className="footer-studio" href={`https://allsystems.uz/${lang}`} target="_blank" rel="noopener noreferrer">
                            <i className="fa-solid fa-code" aria-hidden="true" /> {STUDIO[lang]} — <strong>allsystems.uz</strong>
                        </a>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default Home;
